import { setProcessDocumentFileInfo, setProcessDocumentSelectedFileName, setProcessDocumentUploadIsLoading } from '../../store/processDocument/documentInfo'
import { FileInfo, useUploadFileMutation, useUploadFileRequestMutation } from '../../store/media/file'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useUpdateFileMutation } from '../../store/media/media'
import { selectAccountId } from '../../store/auth/authSlice'

import { FileKind, FileStatus } from '../../models/File'
import React, { useEffect, useRef, useState } from 'react'

export const useDocumentUpload = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerUploadFileRequest] = useUploadFileRequestMutation()
  const [triggerUploadFile] = useUploadFileMutation()
  const [triggerUpdateFile] = useUpdateFileMutation()

  const requestFileUpload = async (file: File) => {
    const body = { fileName: file.name, fileSize: file.size, contentType: file.type, kind: FileKind.DOCUMENT }
    const { data: fileUploadRequestResponse } = await triggerUploadFileRequest({ accountId, body }).unwrap()
    return {
      uploadUrl: fileUploadRequestResponse.fileUploadUrl,
      uploadParameters: fileUploadRequestResponse.fileUploadParameters,
      fileInfo: fileUploadRequestResponse.fileInfo,
    }
  }

  const uploadFile = async (file: File): Promise<FileInfo | null> => {
    dispatch(setProcessDocumentUploadIsLoading(true))
    dispatch(setProcessDocumentSelectedFileName(file.name))
    try {
      const { uploadUrl, uploadParameters, fileInfo } = await requestFileUpload(file)
      const { fileId } = fileInfo
      await triggerUploadFile({ uploadUrl, uploadParameters, file }).unwrap()
      await triggerUpdateFile({ accountId, fileId, body: { status: FileStatus.UPLOADED } }).unwrap()
      dispatch(setProcessDocumentFileInfo(fileInfo))
      return fileInfo
    } catch (e) {
      console.error(e)
      return null
    } finally {
      dispatch(setProcessDocumentUploadIsLoading(false))
    }
  }

  return { uploadFile }
}

export const useSafeTimeout = () => {
  const ref = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearSafeTimeout = () => {
    if (ref.current) {
      clearTimeout(ref.current)
    }
  }

  useEffect(() => {
    return () => {
      clearSafeTimeout()
    }
  }, [])

  const setSafeTimeout = (callback: () => void, delay?: number) => {
    ref.current = setTimeout(callback, delay)
  }

  return { setSafeTimeout, clearSafeTimeout }
}

export const useProcessDocumentFileInput = (supportedMimeTypes: string[], onFileSelected: (file: File) => Promise<void>) => {
  const [isHovering, setIsHovering] = useState<boolean>(false)

  useEffect(() => {
    const handleDrop = (e: DragEvent) => {
      e.preventDefault()
    }

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault()
    }

    window.addEventListener('drop', handleDrop)
    window.addEventListener('dragover', handleDragOver)

    return () => {
      window.removeEventListener('drop', handleDrop)
      window.removeEventListener('dragover', handleDragOver)
    }
  }, [])

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()

    const { files } = e.target
    if (!files?.length) {
      return
    }

    await onFileSelected(files[0])
  }

  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setIsHovering(false)
    const { dataTransfer } = e
    const { files } = dataTransfer
    if (!files.length) {
      return
    }

    const file = files[0]

    if (!supportedMimeTypes.includes(file.type)) {
      return
    }

    await onFileSelected(files[0])
  }

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsHovering(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsHovering(false)
  }

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return {
    isHovering,
    handleChange,
    handleDrop,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
  }
}
